import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Drawer, List, ListItem, ListItemText, ListItemIcon, IconButton, Typography, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SavedItemsGrid from '../Dashboard/UserDashboard/SavedItemsGrid';
import SavedMapItemList from '../Dashboard/UserDashboard/SavedMapItemList';
import SavedItemDetail from '../Dashboard/UserDashboard/SavedItemDetail';
import FollowedReviewersList from '../Dashboard/UserDashboard/FollowedReviewersList';
import { useSavedPanel } from '../../hooks/useSavedPanel';
import MyWorksPanel from '../Dashboard/UserDashboard/MyWorksPanel';
import Map from '../Map/Map';
import Header from '../Header/Header';
import RightSidebar from './RightSidebar';
import BottomMenu from './BottomMenu';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LoginIcon from '@mui/icons-material/Login';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import useTTS from '../../hooks/useTTS';
import { formatEventForTTS } from '../../utils/ttsFormatter';
import menuItems from '../../utils/menuConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import CategorySelectionModal from '../InputPages/CategorySelectionModal';

const DesktopLayout = ({
  coordinates,
  setCoordinates,
  bounds,
  setBounds,
  childClicked,
  setChildClicked,
  handleMarkerClick,
  handleLocationClick,
  currentView,
  setCurrentView,
  type,
  setType,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  followList,
  setFollowList,
  userLocation,
  feedLat,
  feedLng,
  feedRadiusKm,
  onSearch,
  searchQuery, // NEW: Search query to pass to SmartEventList
  hasSearched,
  source,              // NEW: Which collections to search
  excludeCategories,   // NEW: Categories to exclude
  categoryFilter,      // NEW: Category filter from tab config (resolved, for SmartEventList)
  uiCategoryFilter,    // NEW: Raw dropdown state (for Header display)
  setUiCategoryFilter, // NEW: Setter for Header dropdown
  selectedCreator,
  setSelectedCreator,
  creatorProfile,
  onCreatorClick,
  onReviewerSelect,
}) => {
  const { language } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [savedEverOpened, setSavedEverOpened] = useState(false);
  const [savedRefreshKey, setSavedRefreshKey] = useState(0);
  const [showProfile, setShowProfile] = useState(false);
  const [profileEverOpened, setProfileEverOpened] = useState(false);
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);

  // Saved panel (ตาราง / แผนที่ / ติดตาม) — shared logic with the mobile layout
  const savedPanel = useSavedPanel({ isAuthenticated, language, active: showSaved });
  const { savedTab, setSavedTab } = savedPanel;
  // On the grid OR map tab, the saved/reviewer items appear as markers on the
  // always-visible left map (following tab keeps the normal feed).
  const savedMapActive = showSaved && savedTab !== 'following';

  // ผลงานของฉัน: markers for the currently selected category tab.
  const [myWorksMapItems, setMyWorksMapItems] = useState([]);
  const [myWorksFitKey, setMyWorksFitKey] = useState(null);
  const handleMyWorksMapItems = useCallback((items, fitKey) => {
    setMyWorksMapItems(items || []);
    setMyWorksFitKey(fitKey || null);
  }, []);
  const profileMarkersActive = showProfile && myWorksMapItems.length > 0;

  // Clicking a grid preview centers the (always-visible) map on that item.
  const handleSavedGridItemClick = useCallback((item) => {
    // Pan the shared (left) map toward the item, then show its detail inline.
    const lat = Number(item?.latitude);
    const lng = Number(item?.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) setCoordinates({ lat, lng });
    savedPanel.openDetail(item);
  }, [setCoordinates, savedPanel]);

  // A logged-out user pressing Saved / My Works is sent to login; remember which
  // panel they pressed so we can reopen it here after they log in.
  const goToLoginForPanel = useCallback((panel) => {
    try { localStorage.setItem('returnUrl', `${window.location.pathname}?panel=${panel}`); } catch (e) { /* ignore */ }
    // Open login as an overlay over the still-mounted map page so closing it
    // returns here with all state intact.
    navigate('/login', { state: { background: routerLocation } });
  }, [navigate, routerLocation]);

  // Coming back from login with ?panel= → open that panel, then clean the URL.
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(routerLocation.search);
    const panel = params.get('panel');
    if (!panel) return;
    if (panel === 'saved') { setShowSaved(true); setSavedEverOpened(true); setSavedRefreshKey((k) => k + 1); setShowProfile(false); }
    else if (panel === 'profile') { setShowProfile(true); setProfileEverOpened(true); setProfileRefreshKey((k) => k + 1); setShowSaved(false); }
    params.delete('panel');
    const search = params.toString();
    navigate({ pathname: routerLocation.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [isAuthenticated, routerLocation.search, routerLocation.pathname, navigate]);

  // NEW: State to receive items from SmartEventList
  const [displayedItems, setDisplayedItems] = useState([]);

  // TTS functionality
  const { speak, stop, isSupported, availableVoices, selectedVoiceName, setVoice, audioOutputDevices, selectedOutputDeviceId, setOutputDevice } = useTTS(language);
  const [isTTSAutoEnabled, setIsTTSAutoEnabled] = useState(false);
  const previousChildClickedRef = useRef(childClicked);

  const menuConfig = menuItems(isAuthenticated, language);

  const handleMenuItemClick = async (item) => {
    if (item.action === 'logout') {
      await logout();
      navigate('/');
    } else if (item.action === 'add-data') {
      setCategoryModalOpen(true);
    } else if (item.external) {
      window.open(item.path, '_blank');
    } else if (item.path === '/tour-guide') {
      // Pass origin so the dashboard back button returns here
      navigate(item.path, { state: { from: routerLocation.pathname + routerLocation.search } });
    } else {
      navigate(item.path);
    }
    setDrawerOpen(false);
  };

  const getMenuItemIcon = (label) => {
    const l = (label || '').toLowerCase();
    if (l.includes('scan') || l.includes('qr') || l.includes('扫描') || l.includes('สแกน')) {
      return <QrCodeScannerIcon />;
    }
    if (l.includes('travy') || l.includes('tvent') || l.includes('旅游团') || l.includes('ทัวร์กลุ่ม')) {
      return <PeopleAltIcon />;
    }
    if (l.includes('event') || l.includes('กิจกรรม') || l.includes('活动') || l.includes('adding') || l.includes('添加') || l.includes('เพิ่ม')) {
      return <PlaylistAddIcon />;
    }
    if (l.includes('dashboard') || l.includes('แดชบอร์ด') || l.includes('仪表板')) {
      return <DashboardIcon />;
    }
    if (l.includes('login') || l.includes('เข้าสู่ระบบ') || l.includes('登录') || l.includes('sign up') || l.includes('注册') || l.includes('สมัครสมาชิก')) {
      return <LoginIcon />;
    }
    return null;
  };

  // NEW: Handle items updates from SmartEventList
  const handleItemsChange = useCallback((items) => {
    setDisplayedItems(items);
  }, []);

  // NEW: Handle map sync when active item changes in list
  // NOTE: Does NOT set childClicked to prevent feedback loop
  // (onActiveIndexChange already reports the index separately)
  const handleMapSync = useCallback((lat, lng, index) => {
    // Don't let list items far from the user's GPS location override the map center.
    // This prevents Bangkok items from pulling the map away from Hat Yai (or any
    // non-Bangkok location) when SmartEventList auto-fires on initial load.
    // 2 degrees ≈ 220 km — items further than that won't re-center the map.
    if (userLocation) {
      const dLat = Math.abs(lat - userLocation.lat);
      const dLng = Math.abs(lng - userLocation.lng);
      if (dLat > 2 || dLng > 2) return;
    }
    setCoordinates({ lat, lng });
  }, [setCoordinates, userLocation]);

  // Handler for when active index changes in the list (kept for compatibility)
  // STEP 6: Ignore active index changes while stabilizing
  const handleActiveIndexChange = useCallback((index, isStabilizing = false, isAuthoritative = false) => {
    if (isStabilizing) {
      // we are in the middle of a window shift → DO NOT update map, DO NOT set childClicked
      console.log('[DesktopLayout] (ignored) active index', index, 'because list is stabilizing');
      return;
    }

    // extra guard: sometimes right after shift we get index 0 from a half-baked window
    if (index === 0 && !isAuthoritative) {
      console.log('[DesktopLayout] (ignored) weak index 0 after shift');
      return;
    }

    const place = displayedItems[index];
    if (!place) return;

    // Guard: don't update if index hasn't changed (prevents feedback loops and unnecessary re-renders)
    if (childClicked === index) {
      // console.log('[DesktopLayout] Skipping childClicked update - already at index', index);
      return;
    }

    console.log('[DesktopLayout] Active index changed to', index, '- updating childClicked and map position');

    // select the event in the list
    setChildClicked(index);

    // move map to this event
    setCoordinates({
      lat: place.latitude,
      lng: place.longitude,
    });
  }, [displayedItems, childClicked, setChildClicked, setCoordinates]);

  // Get selected event ID from childClicked index
  const selectedEventId = childClicked !== null && displayedItems[childClicked]
    ? (displayedItems[childClicked].event_id || displayedItems[childClicked].id)
    : null;

  // TTS toggle handler
  const handleTTSToggle = useCallback(() => {
    if (isTTSAutoEnabled) {
      // Disable auto-read mode
      setIsTTSAutoEnabled(false);
      stop();
    } else {
      // Enable auto-read mode
      setIsTTSAutoEnabled(true);
      
      // Play a short silent utterance to unlock speech synthesis on iOS/Safari/macOS
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        const unlockUtterance = new SpeechSynthesisUtterance('');
        window.speechSynthesis.speak(unlockUtterance);
      }

      // If no item is selected, automatically select the first one!
      let targetIndex = childClicked;
      if (targetIndex === null && displayedItems && displayedItems.length > 0) {
        targetIndex = 0;
        setChildClicked(0);
      }

      const focusedItem = displayedItems[targetIndex];
      if (focusedItem) {
        const text = formatEventForTTS(focusedItem, language);
        speak(text);
      }
    }
  }, [isTTSAutoEnabled, stop, speak, displayedItems, childClicked, language, setChildClicked]);

  const handleTTSRestart = useCallback(() => {
    if (!isTTSAutoEnabled) return;
    const idx = childClicked !== null ? childClicked : 0;
    const focusedItem = displayedItems[idx];
    if (focusedItem) {
      const text = formatEventForTTS(focusedItem, language);
      speak(text);
    }
  }, [isTTSAutoEnabled, displayedItems, childClicked, language, speak]);

  const previousLanguageRef = useRef(language);

  // Auto-read new middle item when scrolling or when language changes (if TTS is enabled)
  useEffect(() => {
    // Skip on initial mount or when childClicked becomes null
    if (previousChildClickedRef.current === null && childClicked === null) {
      return;
    }

    const languageChanged = previousLanguageRef.current !== language;
    previousLanguageRef.current = language;

    // Auto-read if TTS mode is enabled AND (childClicked changed OR language changed)
    if (isTTSAutoEnabled && (childClicked !== previousChildClickedRef.current || languageChanged)) {
      previousChildClickedRef.current = childClicked;

      // Stop current reading
      stop();

      // Start reading current item
      if (childClicked !== null && displayedItems[childClicked]) {
        const focusedItem = displayedItems[childClicked];
        const text = formatEventForTTS(focusedItem, language);
        speak(text);
      }
    } else {
      previousChildClickedRef.current = childClicked;
    }
  }, [childClicked, isTTSAutoEnabled, displayedItems, language, speak, stop]);

  const isTTSDisabled = !isSupported || !displayedItems || displayedItems.length === 0;

  return (
    <Box sx={{ 
      height: '100vh',
      width: '100vw',
      display: 'flex',
      flexDirection: 'row',
      backgroundColor: '#f6f7fb'
    }}>
      {/* Map Section - 60% width */}
      <Box sx={{
        width: '60%',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Map
          setCoordinates={setCoordinates}
          setBounds={setBounds}
          bounds={bounds}
          coordinates={coordinates}
          places={savedMapActive ? savedPanel.mapItems : (profileMarkersActive ? myWorksMapItems : displayedItems)}
          selectedEventId={(savedMapActive || profileMarkersActive) ? null : selectedEventId} // Pass actual event ID, not index
          onMarkerClick={handleMarkerClick}
          userLocation={userLocation}
          onSearch={onSearch}
          disablePopups={false}
          onCreatorClick={onCreatorClick}
          fitToPlacesKey={savedMapActive
            ? savedPanel.mapFitKey
            : profileMarkersActive
              ? myWorksFitKey
              : (type === 'Reviewers' && selectedCreator?.id ? `reviewer-${selectedCreator.id}` : null)}
        />

        {/* Header overlay on map */}
        <Box sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
          borderRadius: '24px',
          boxShadow: '0 18px 42px rgba(31, 41, 55, 0.16)',
          border: '1px solid rgba(255,255,255,0.72)',
          backdropFilter: 'blur(16px)',
          maxWidth: 'calc(100% - 32px)'
        }}>
          <Header
            setCoordinates={setCoordinates}
            type={type}
            setType={setType}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            mapBounds={bounds}
            onSearch={onSearch}
            categoryFilter={uiCategoryFilter}
            setCategoryFilter={setUiCategoryFilter}
          />

        {/* Hamburger menu removed for desktop view only */}

        {/* Exclamation mark moved to Header component next to language flags */}
        </Box>

      </Box>

      {/* Right Sidebar - expanded width, accounting for vertical menu */}
      <Box
        sx={{
          width: 'calc(40% - 75px)', // Further reduced margin for vertical menu
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          marginRight: '75px', // Further reduced space for the vertical menu
          backgroundColor: '#ffffff',
          boxShadow: '-14px 0 34px rgba(31, 41, 55, 0.08)',
          zIndex: 2
        }}
        onWheel={() => setFollowList(true)}
        onTouchStart={() => setFollowList(true)}
      >
        {/* Saved panel — mounted only when first opened, hidden when not active */}
        {savedEverOpened && (
          <Box sx={{ height: '100%', display: showSaved ? 'flex' : 'none', flexDirection: 'column' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #f0f0f0',
              flexShrink: 0
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" onClick={() => setShowSaved(false)}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.1rem' }}>
                  {language === 'TH' ? 'ที่บันทึกไว้' : language === 'CN' ? '已保存' : 'Saved'}
                </Typography>
              </Box>

              {/* Segmented Control Selector Tabs: ตาราง / แผนที่ / ติดตาม */}
              <Box sx={{
                display: 'flex',
                backgroundColor: '#f1f5f9',
                borderRadius: '20px',
                p: 0.5,
                border: '1px solid #e2e8f0'
              }}>
                {[
                  { id: 'grid', label: language === 'TH' ? 'ตาราง' : language === 'CN' ? '网格' : 'Grid' },
                  { id: 'map', label: language === 'TH' ? 'แผนที่' : language === 'CN' ? '地图' : 'Map' },
                  { id: 'following', label: language === 'TH' ? 'ติดตาม' : language === 'CN' ? '关注' : 'Following' },
                ].map((tab) => (
                  <Button
                    key={tab.id}
                    size="small"
                    onClick={() => savedPanel.changeTab(tab.id)}
                    sx={{
                      borderRadius: '16px',
                      fontSize: '0.76rem',
                      fontWeight: 750,
                      textTransform: 'none',
                      py: 0.4,
                      px: 1.6,
                      minWidth: 0,
                      color: savedTab === tab.id ? '#ffffff' : '#475569',
                      backgroundColor: savedTab === tab.id ? '#7c3aed' : 'transparent',
                      boxShadow: savedTab === tab.id ? '0 2px 8px rgba(124, 58, 237, 0.25)' : 'none',
                      '&:hover': {
                        backgroundColor: savedTab === tab.id ? '#6d28d9' : 'rgba(0,0,0,0.04)'
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    {tab.label}
                  </Button>
                ))}
              </Box>
            </Box>

            {savedPanel.selectedReviewer && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 2,
                py: 1,
                bgcolor: 'rgba(124, 58, 237, 0.06)',
                borderBottom: '1px solid rgba(124, 58, 237, 0.15)',
                flexShrink: 0
              }}>
                <Typography sx={{ fontSize: '0.85rem', color: '#6d28d9', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {language === 'TH' ? 'กำลังแสดงรายการของ ' : language === 'CN' ? '正在显示 ' : 'Showing items by '}
                  {savedPanel.selectedReviewer.name}
                </Typography>
                <Button
                  size="small"
                  onClick={() => savedPanel.clearReviewer()}
                  sx={{
                    flexShrink: 0,
                    borderRadius: '16px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    color: '#7c3aed',
                    '&:hover': { backgroundColor: 'rgba(124, 58, 237, 0.08)' }
                  }}
                >
                  {language === 'TH' ? 'กลับไปของฉัน' : language === 'CN' ? '返回我的' : 'Back to mine'}
                </Button>
              </Box>
            )}

            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {savedPanel.detailItem && savedTab !== 'following' ? (
                <SavedItemDetail
                  item={savedPanel.detailItem}
                  language={language}
                  onBack={savedPanel.closeDetail}
                  onViewOnMap={(lat, lng) => { setCoordinates({ lat, lng }); savedPanel.closeDetail(); }}
                />
              ) : savedTab === 'grid' ? (
                <SavedItemsGrid
                  items={savedPanel.gridItems}
                  loading={savedPanel.gridLoading}
                  language={language}
                  onItemClick={handleSavedGridItemClick}
                  emptyText={savedPanel.selectedReviewer
                    ? (language === 'TH' ? 'ยังไม่มีรายการของผู้รีวิวคนนี้' : language === 'CN' ? '该评论者暂无内容' : 'This reviewer has no items yet')
                    : undefined}
                />
              ) : savedTab === 'map' ? (
                savedPanel.mapItems.length > 0 ? (
                  <Box>
                    <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: '0.8rem', color: 'text.secondary', fontWeight: 700 }}>
                      {language === 'TH' ? `แสดง ${savedPanel.mapItems.length} รายการบนแผนที่ทางซ้าย` : language === 'CN' ? `已在左侧地图显示 ${savedPanel.mapItems.length} 个项目` : `Showing ${savedPanel.mapItems.length} items on the map`}
                    </Typography>
                    <SavedMapItemList items={savedPanel.mapItems} onItemClick={handleSavedGridItemClick} />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', px: 3, gap: 1 }}>
                    <Typography sx={{ fontSize: '2rem' }}>🗺️</Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', fontWeight: 600 }}>
                      {language === 'TH' ? 'ไม่มีตำแหน่งที่จะแสดงบนแผนที่' : language === 'CN' ? '没有可显示的位置' : 'No locations to show on the map'}
                    </Typography>
                  </Box>
                )
              ) : (
                <FollowedReviewersList
                  creators={savedPanel.followedCreators}
                  loading={savedPanel.followedLoading}
                  language={language}
                  onSelect={(creator) => savedPanel.selectReviewer(creator)}
                />
              )}
            </Box>
          </Box>
        )}
        {/* My Works panel — mounted only when first opened, hidden when not active */}
        {profileEverOpened && (
          <Box sx={{ height: '100%', display: showProfile ? 'flex' : 'none', flexDirection: 'column' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #f0f0f0',
              flexShrink: 0
            }}>
              <IconButton size="small" onClick={() => setShowProfile(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.1rem' }}>
                {language === 'CN' ? '我的作品' : language === 'EN' ? 'My Works' : 'ผลงานของฉัน'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              <MyWorksPanel language={language} refreshKey={profileRefreshKey} onMapItemsChange={handleMyWorksMapItems} />
            </Box>
          </Box>
        )}
        {/* RightSidebar — always mounted, hidden behind panels to preserve scroll/list state */}
        <Box sx={{ display: (showSaved || showProfile) ? 'none' : 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
          <RightSidebar
            childClicked={childClicked}
            onLocationClick={handleLocationClick}
            type={type}
            setType={setType}
            language={language}
            searchQuery={searchQuery}
            startDate={startDate}
            endDate={endDate}
            source={source}
            excludeCategories={excludeCategories}
            categoryFilter={categoryFilter}
            onActiveIndexChange={handleActiveIndexChange}
            onItemsChange={handleItemsChange}
            onMapSyncNeeded={handleMapSync}
            userLocation={userLocation}
            feedLat={feedLat}
            feedLng={feedLng}
            feedRadiusKm={feedRadiusKm}
            selectedCreator={selectedCreator}
            setSelectedCreator={setSelectedCreator}
            creatorProfile={creatorProfile}
            onReviewerSelect={onReviewerSelect}
            onCreatorClick={onCreatorClick}
          />
        </Box>
      </Box>

      {/* Vertical Bottom Menu - positioned at right edge */}
      <Box sx={{ 
        position: 'fixed',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1000,
        backgroundColor: 'rgba(255,255,255,0.96)',
        borderLeft: '1px solid rgba(229,231,235,0.9)',
        borderRadius: '18px 0 0 18px',
        boxShadow: '-10px 0 28px rgba(31, 41, 55, 0.12)',
        backdropFilter: 'blur(14px)'
      }}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column'
        }}>
          <BottomMenu
            onShowSaved={() => { if (!isAuthenticated) { goToLoginForPanel('saved'); return; } setShowSaved(true); setSavedEverOpened(true); setSavedRefreshKey((k) => k + 1); setShowProfile(false); }}
            savedActive={showSaved}
            onShowProfile={() => {
              if (!isAuthenticated) { goToLoginForPanel('profile'); return; }
              setShowProfile(true); setProfileEverOpened(true); setProfileRefreshKey((k) => k + 1); setShowSaved(false);
            }}
            profileActive={showProfile}
          />
        </Box>
      </Box>
      {/* Global Hamburger Drawer Menu */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={() => setDrawerOpen(false)}
          onKeyDown={() => setDrawerOpen(false)}
        >
          <List>
            {menuConfig.map((item, index) => {
              const icon = getMenuItemIcon(item.label);
              return (
                <ListItem key={index} button onClick={() => handleMenuItemClick(item)}>
                  {icon && (
                    <ListItemIcon>
                      {icon}
                    </ListItemIcon>
                  )}
                  <ListItemText primary={item.label} />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>

      {/* Category Selection Modal */}
      <CategorySelectionModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        language={language}
      />

      {/* Exclamation mark menu functionality removed - now just an info button in header */}
    </Box>
  );
};

export default DesktopLayout;
